import { useMemo, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { toast } from 'react-toastify';

import { useBulkAddOrderNotes } from '../../../order-notes/api';
import { Modal } from '../../../../components/Modal/Modal';
import { distinctOrderIds, orderIdOf } from '../grain';
import type { BulkAction, BulkActionBarContext } from '../types';

// Add the same note to every selected order thread. Notes are per-Order, so the
// load selection is deduped to distinct orders (and loads with no order skipped).
function AddNoteTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const { rows, count } = ctx;
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');

  const bulkAddNote = useBulkAddOrderNotes();
  const isApplying = bulkAddNote.isPending;

  const orderIds = useMemo(() => distinctOrderIds(rows), [rows]);
  const rowsWithoutOrderId = count - rows.filter((row) => orderIdOf(row)).length;
  const duplicateOrderRows = count - rowsWithoutOrderId - orderIds.length;

  const close = () => {
    if (!isApplying) setOpen(false);
  };

  const apply = async () => {
    const text = body.trim();
    if (!text || orderIds.length === 0) return;

    try {
      const result = await bulkAddNote.mutateAsync({ body: text, orderIds });
      toast.success(
        `Note added to ${result.createdCount} order${result.createdCount === 1 ? '' : 's'}.`
      );
      setOpen(false);
      setBody('');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to add the note to the selected loads.');
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => setOpen(true)}
        disabled={isApplying || orderIds.length === 0}
      >
        <MessageSquarePlus size={14} />
        Add note
      </button>

      <Modal
        isOpen={open}
        onClose={close}
        title={`Add a note to ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`}
        size="sm"
        footer={
          <>
            <button className="btn btn--ghost" onClick={close} disabled={isApplying}>
              Cancel
            </button>

            <button
              className="btn btn--primary"
              onClick={apply}
              disabled={isApplying || !body.trim() || orderIds.length === 0}
            >
              {isApplying
                ? 'Adding…'
                : `Add to ${orderIds.length} order${orderIds.length === 1 ? '' : 's'}`}
            </button>
          </>
        }
      >
        <p>
          The same note will be added to every selected order thread. Customers can see these notes.
        </p>

        {duplicateOrderRows > 0 && (
          <div className="bulk-confirm__warning">
            {duplicateOrderRows} selected load
            {duplicateOrderRows === 1 ? '' : 's'} share an order with another selected load, so each
            shared order will receive the note only once.
          </div>
        )}

        {rowsWithoutOrderId > 0 && (
          <div className="bulk-confirm__warning">
            {rowsWithoutOrderId} selected load
            {rowsWithoutOrderId === 1 ? '' : 's'} will be skipped because no order ID is available.
          </div>
        )}

        <label
          className="bulk-bar__note-label"
          htmlFor="bulk-order-note"
          style={{ display: 'block', marginTop: 16, marginBottom: 6, fontWeight: 600 }}
        >
          Note
        </label>

        <textarea
          id="bulk-order-note"
          className="bulk-bar__note-input"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Enter the note to add to all selected loads…"
          rows={6}
          maxLength={5000}
          disabled={isApplying}
          autoFocus
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
        />

        <div
          className="bulk-bar__note-count"
          style={{ marginTop: 4, textAlign: 'right', fontSize: 12, opacity: 0.7 }}
        >
          {body.length}/5000
        </div>
      </Modal>
    </>
  );
}

export const AddNoteAction: BulkAction = {
  id: 'add-note',
  isAvailable: () => true,
  Component: AddNoteTrigger,
};
